import { useEffect, useState } from "react";
import { Button, Card, InputNumber, Select, Space, Switch, Typography, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { api } from "../../helpers/api";
import { useApi } from "../../helpers/useApi";
import { mvrToLaari, laariToMvr } from "../../helpers/format";
import type { ApprovalStep, ApprovalWorkflow, Role } from "../../api-models";

const SCOPE_RULES = ["ANY", "SITE", "SOURCE_SITE", "DEST_SITE", "WAREHOUSE", "PROJECT"];

export default function WorkflowsPage() {
  const { request, loading } = useApi();
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  async function load() {
    const [w, r] = await Promise.all([api.get<ApprovalWorkflow[]>("/settings/workflows"), api.get<Role[]>("/roles")]);
    setWorkflows(w.data);
    setRoles(r.data);
  }

  useEffect(() => {
    load();
  }, []);

  function updateWorkflow(id: number, patch: Partial<ApprovalWorkflow>) {
    setWorkflows((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }

  function updateStep(workflowId: number, index: number, patch: Partial<ApprovalStep>) {
    setWorkflows((prev) =>
      prev.map((w) => (w.id === workflowId ? { ...w, steps: (w.steps ?? []).map((s, i) => (i === index ? { ...s, ...patch } : s)) } : w)),
    );
  }

  function addStep(workflowId: number) {
    setWorkflows((prev) =>
      prev.map((w) =>
        w.id === workflowId
          ? {
              ...w,
              steps: [
                ...(w.steps ?? []),
                {
                  id: 0, createdAt: "", updatedAt: "", workflowId, stepOrder: (w.steps?.length ?? 0) + 1,
                  roleId: roles[0]?.id ?? 0, scopeRule: "ANY" as const, minAmountLaari: undefined,
                  maxAmountLaari: undefined, currency: "", requiredApprovalCount: 1,
                  effectiveFrom: undefined, effectiveTo: undefined, projectId: undefined,
                },
              ],
            }
          : w,
      ),
    );
  }

  function removeStep(workflowId: number, index: number) {
    setWorkflows((prev) => prev.map((w) => (w.id === workflowId ? { ...w, steps: (w.steps ?? []).filter((_, i) => i !== index) } : w)));
  }

  async function save() {
    await request({ method: "PUT", url: "/settings/workflows", data: workflows });
    message.success("Workflows saved.");
    load();
  }

  return (
    <div>
      <PageHeader
        title="Approval Workflows"
        extra={
          <Button type="primary" onClick={save} loading={loading}>
            Save all
          </Button>
        }
      />
      <Typography.Paragraph type="secondary">
        Sequential steps only. A step is skipped when the document's amount is below its threshold. Editing here does not affect
        documents already submitted (steps are snapshotted at submit time).
      </Typography.Paragraph>
      {workflows.map((wf) => (
        <Card
          key={wf.id}
          title={wf.name}
          extra={<Switch checked={wf.active} onChange={(v) => updateWorkflow(wf.id, { active: v })} checkedChildren="Active" unCheckedChildren="Inactive" />}
          style={{ marginBottom: 16 }}
        >
          {(wf.steps ?? [])
            .slice()
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map((step, index) => (
              <Space key={index} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                <span>Step {step.stepOrder}</span>
                <Select
                  style={{ width: 180 }}
                  value={step.roleId}
                  options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  onChange={(v) => updateStep(wf.id, index, { roleId: v })}
                />
                <Select
                  style={{ width: 140 }}
                  value={step.scopeRule}
                  options={SCOPE_RULES.map((s) => ({ value: s, label: s }))}
                  onChange={(v) => updateStep(wf.id, index, { scopeRule: v })}
                />
                <InputNumber
                  placeholder="Min amount (MVR)"
                  style={{ width: 160 }}
                  value={laariToMvr(step.minAmountLaari ?? undefined)}
                  onChange={(v) => updateStep(wf.id, index, { minAmountLaari: v != null ? mvrToLaari(v) : undefined })}
                />
                <InputNumber
                  addonBefore="Required"
                  min={1}
                  style={{ width: 140 }}
                  value={step.requiredApprovalCount || 1}
                  onChange={(v) => updateStep(wf.id, index, { requiredApprovalCount: v ?? 1 })}
                />
                <Button icon={<DeleteOutlined />} danger type="text" onClick={() => removeStep(wf.id, index)} />
              </Space>
            ))}
          <Button icon={<PlusOutlined />} onClick={() => addStep(wf.id)}>
            Add step
          </Button>
        </Card>
      ))}
    </div>
  );
}
